import React, {useContext, useState, useRef, useEffect} from 'react';
import {Context} from '../../context';
import {getDateString} from '../../helperFunctions';
import Button from '../UI/Button';

function ReviewInfoAndConfirm(props) {
  const {
    accessRequestId,
    setTransitionComplete,
    setAccountLockedAfterTransfer
  } = props;
  const {
    localeText,
    account,
    fetchAccount,
    setLoading,
    loading,
    transitionGetProducts,
    transitionTransferProducts,
    transitionConfirm,
    signOut
  } = useContext(Context);
  const [oldAccountProducts, setOldAccountProducts] = useState([]);
  const completedCheckboxDate = useRef(false);
  const completedCheckboxInfoConfirmation = useRef(false);
  const completedCheckboxTermsAndConditions = useRef(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    (async () => {
      const res = await transitionGetProducts({accessRequestId});

      props.handleErrors(res, setErrors);

      if (!res?.data?.getProducts) {
        return;
      }

      setOldAccountProducts(res.data.getProducts);
    })();
  }, []);

  const performTransition = async () => {
    setErrors([]);
    setLoading(true);
    if (
      !completedCheckboxDate.current.checked ||
      !completedCheckboxInfoConfirmation.current.checked ||
      !completedCheckboxTermsAndConditions.current.checked
    ) {
      setErrors(prev => {
        return [
          ...prev,
          {
            slug: 'rw_msg_migration_confirm_checkboxes',
            default: 'Please confirm checkboxes to proceed'
          }
        ];
      });
      setLoading(false);
      return;
    }

    const transferRes = await transitionTransferProducts({accessRequestId});

    if (transferRes?.data?.transferProducts) {
      transferRes.data.transferProducts
        .filter(
          ({operational_status_code}) => operational_status_code === 'hotlist'
        )
        .map(() => {
          setAccountLockedAfterTransfer(true);
          signOut();
          setErrors({
            slug: 'serr_denied-account-locked',
            default: 'Account has been locked. Please talk to support.'
          });
        });
    }

    props.handleErrors(transferRes, setErrors);

    if (!transferRes?.data?.transferProducts) {
      return;
    }
    const confirm = await transitionConfirm({
      accessRequestId,
      transactionId: transferRes.data.transferProducts[0].transaction_id
    });

    props.handleErrors(confirm, setErrors);

    if (!confirm?.data?.confirm?.success) {
      return;
    }

    fetchAccount();
    setLoading(false);
    setTransitionComplete(true);
  };

  return (
    <div className=''>
      <div className='w-full flex justify-between'>
        <table>
          <tbody>
            <tr>
              <td className='font-bold pr-4'>
                {localeText('rw_title_name', 'Name')}
              </td>
              <td>
                {`${account?.profile?.firstName} ${account?.profile?.lastName}`}
              </td>
            </tr>
            <tr>
              <td className='font-bold'>
                {localeText('rw_title_email', 'Email')}
              </td>
              <td>{account?.profile?.email}</td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <td className='font-bold pr-4'>
                {localeText(
                  'rw_title_date_of_acc_info',
                  'Date of Account Information'
                )}
              </td>
              <td>{getDateString(new Date())}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <table className='mt-8 '>
        <tbody className='[&>*:nth-child(even)]:bg-blue-200'>
          <tr className='font-bold'>
            <td className='px-6'>
              {localeText('rw_title_serial_num', 'Serial Number')}
            </td>
            <td className='pr-6'>
              {localeText('rw_title_purchase_value', 'Purchase Value')}
            </td>
            <td className='pr-6'>
              {localeText('rw_title_bonus', 'Bonus Value')}
            </td>
            <td className='pr-6'>{localeText('rw_title_total', 'Total')}</td>
            <td className='pr-6'>
              {localeText('rw_title_product', 'Product')}
            </td>
            <td className='pr-6'>
              {localeText('rw_title_pass_start_date', 'Pass Start Date')}
            </td>
            <td className='pr-6'>
              {localeText('rw_title_pass_expiry_date', 'Pass Expiry Date')}
            </td>
          </tr>
          {oldAccountProducts.length > 0 &&
            oldAccountProducts.map((product, index) => (
              <tr key={index} className=''>
                <td className='px-6 py-1'>{product.fare_media_number}</td>
                <td className='pr-6 py-1'>${product.purchase_amount}</td>
                <td className='pr-6 py-1'>${product.bonus_amount}</td>
                <td className='pr-6 py-1'>${product.total_amount}</td>
                <td className='pr-6 py-1'>{product.product}</td>
                <td className='pr-6 py-1'>
                  {getDateString(product.start_date)}
                </td>
                <td className='pr-6'>
                  {getDateString(product.expiration_date)}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <div className='my-4 mt-10'>
        <input type='checkbox' ref={completedCheckboxDate} />
        <label className='ml-2' htmlFor=''>
          {localeText(
            'rw_title_frag_agree_accurate_as_of',
            'I agree this is accurate as of'
          )}{' '}
          {getDateString(new Date())}.
        </label>
      </div>
      <div className='my-4'>
        <input type='checkbox' ref={completedCheckboxInfoConfirmation} />
        <label className='ml-2' htmlFor=''>
          {localeText(
            'rw_title_certify_acc_info_matches',
            'I certify that this is my Account and these are the cards that are associated with the account.'
          )}
        </label>
      </div>
      <div className='my-4'>
        <input type='checkbox' ref={completedCheckboxTermsAndConditions} />
        <label className='ml-2' htmlFor=''>
          {localeText('rw_title_frag_i_agree_to_the', 'I agree to the')}{' '}
          <a
            className='font-medium text-blue-600 dark:text-blue-500 hover:underline'
            href='terms-and-conditions'
            target='_blank'
            rel='noreferrer'
          >
            {localeText(
              'rw_title_frag_terms_and_conditions',
              'Terms and Conditions.'
            )}
          </a>
          .
        </label>
      </div>
      {errors.length > 0 &&
        errors.map((error, index) => (
          <div key={index} className='text-red text-xl my-4'>
            {localeText(error.slug, error.default)}
          </div>
        ))}
      <Button
        buttonId='migration-confirm'
        className='w-96 mr-4 mt-4'
        loading={loading}
        onClick={performTransition}
      >
        {localeText('rw_button_migrate', 'Migrate')}
      </Button>
      <Button
        buttonId='migration-cancel'
        className='w-96'
        loading={loading}
        onClick={() => window.location.reload()}
      >
        {localeText('rw_button_cancel', 'Cancel')}
      </Button>
    </div>
  );
}

export default ReviewInfoAndConfirm;
